import { CheckCircle2, CircleHelp, Expand, Loader2, Pause, Play, RotateCcw, Settings2, Shrink, TimerReset, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { ApiService } from '../../../services/api';
import {
  ACCENT_COLOR_OPTIONS,
  FONT_FAMILY_OPTIONS,
  FONT_SIZE_LABELS,
  FONT_SIZE_OPTIONS,
  ModeIllustration,
  SidebarPreparationCard,
  formatClock,
  getModeCanonicalKey,
  getThemeChromeButtonStyle,
  getThemeChromeTextStyle,
  getThemedBadgeStyle,
  getThemedLabelStyle,
  getThemedOptionButtonStyle,
  getThemedSwatchStyle,
  resolveAccentColor,
  type ModeConfig,
  type PilotSettings,
} from './speedReadingShared';

type AttentionMode = 'rengi-bul' | 'harf-sayma' | 'sayi-sayma' | 'dikkat-puzzle' | 'iki-resim-arasindaki-fark';
type AttentionDifficulty = 'kolay' | 'orta' | 'zor';
type AttentionPhase = 'idle' | 'countdown' | 'running' | 'answer' | 'review';
const EXERCISE_TOOL_BUTTON_CLASS = 'inline-flex h-10 w-10 items-center justify-center rounded-xl text-[#2f7188] transition hover:text-[#174e67] disabled:cursor-not-allowed disabled:opacity-45';

type ColorChoice = {
  key: string;
  label: string;
  hex: string;
};

type ColorFindItem = {
  id: string;
  text: string;
  textColorHex: string;
  displayDurationMs: number;
  answerKey?: string;
  answerText?: string;
};

type CountingBoard = {
  lineLength?: number;
  tokensPerLine?: number;
  lines: string[];
};

type PuzzleCell = {
  row: number;
  column: number;
  missing?: boolean;
  shape?: string;
  colorKey?: string;
  colorHex?: string;
};

type PuzzleOption = {
  id: string;
  shape: string;
  colorKey: string;
  colorHex: string;
};

type SceneCell = PuzzleCell;

type AttentionExercisePayload = {
  title: string;
  type: AttentionMode;
  prompt: string;
  durationSeconds?: number;
  answerInputRevealAfterSeconds?: number;
  answerPrompt?: string;
  target?: string;
  presentation?: string;
  choices?: ColorChoice[];
  items?: ColorFindItem[];
  board?: CountingBoard;
  answer?: number | { optionId?: string | null; cell?: PuzzleCell };
  grid?: PuzzleCell[][];
  options?: PuzzleOption[];
  differenceCount?: number;
  leftScene?: {
    rows: number;
    columns: number;
    cells: SceneCell[];
  };
  rightScene?: {
    rows: number;
    columns: number;
    cells: SceneCell[];
  };
  differences?: Array<{ row: number; column: number }>;
};

type AttentionApiResponse = {
  mode: AttentionMode;
  title: string;
  difficulty: AttentionDifficulty;
  settings?: {
    durationSeconds?: number;
    itemCount?: number;
  };
  exercise: AttentionExercisePayload;
};

type AttentionSettings = {
  difficulty: AttentionDifficulty;
  durationSeconds: number;
  colorDisplayMs: number;
  fontSize: PilotSettings['fontSize'];
  fontFamily: PilotSettings['fontFamily'];
  accentColor: PilotSettings['accentColor'];
  enableCountdown: boolean;
};

const DIFFICULTY_OPTIONS: Array<{ value: AttentionDifficulty; label: string }> = [
  { value: 'kolay', label: 'Kolay' },
  { value: 'orta', label: 'Orta' },
  { value: 'zor', label: 'Zor' },
];

const DURATION_OPTIONS = [30, 45, 60, 90, 120] as const;
const COLOR_SPEED_MIN_MS = 100;
const COLOR_SPEED_MAX_MS = 1000;
const COLOR_SPEED_STEP_MS = 50;

const ATTENTION_INFO: Record<AttentionMode, string[]> = {
  'rengi-bul': [
    'Ekranda tek tek renk isimleri belirecek. Cevap verirken kelimenin anlamını değil yazının rengini seçin.',
    'Bu çalışma seçici dikkati güçlendirir; acele ederken otomatik okuma tepkisini bastırmanız gerekir.',
  ],
  'harf-sayma': [
    'Süre boyunca panodaki hedef harfi sayın. Süre dolduğunda cevap alanı açılır.',
    'Benzer harfleri ayırmak için satır satır ilerleyin ve gözünüzü aynı satıra geri döndürmemeye çalışın.',
  ],
  'sayi-sayma': [
    'Süre boyunca panodaki hedef sayıyı sayın. Süre dolduğunda bulduğunuz adedi yazın.',
    'İki basamaklı sayıları blok halinde tarayarak seçici görsel dikkatinizi çalıştırırsınız.',
  ],
  'dikkat-puzzle': [
    'Izgaradaki örüntüyü inceleyin ve eksik parçayı seçenekler arasından seçin.',
    'Şekil ve renk düzenini birlikte takip etmek görsel akıl yürütme ve odak sürekliliğini destekler.',
  ],
  'iki-resim-arasindaki-fark': [
    'Yan yana verilen iki görselde değişen hücreleri seçin.',
    'Renk ve şekil değişimlerini karşılaştırarak ayrıntı fark etme becerisini çalıştırırsınız.',
  ],
};

const COUNTING_MODES = new Set<AttentionMode>(['harf-sayma', 'sayi-sayma']);
const STATIC_ATTENTION_MODES = new Set<AttentionMode>(['dikkat-puzzle', 'iki-resim-arasindaki-fark']);

const normalizeAttentionMode = (mode: ModeConfig): AttentionMode => {
  const key = getModeCanonicalKey(mode);
  if (key === 'sayi-sayma') return 'sayi-sayma';
  if (key === 'harf-sayma') return 'harf-sayma';
  if (key === 'dikkat-puzzle') return 'dikkat-puzzle';
  if (key === 'iki-resim-arasindaki-fark') return 'iki-resim-arasindaki-fark';
  return 'rengi-bul';
};

const shapeLabelMap: Record<string, string> = {
  circle: 'Daire',
  square: 'Kare',
  triangle: 'Üçgen',
  diamond: 'Baklava',
  hexagon: 'Altıgen',
  star: 'Yıldız',
};

const shapeGlyphMap: Record<string, string> = {
  circle: '●',
  square: '■',
  triangle: '▲',
  diamond: '◆',
  hexagon: '⬢',
  star: '★',
};

const shapeGlyph = (shape?: string) => shapeGlyphMap[String(shape || '')] || '●';
const shapeLabel = (shape?: string) => shapeLabelMap[String(shape || '')] || 'Şekil';

const cellKey = (row: number, column: number) => `${row}:${column}`;

const durationLabel = (seconds: number) => `${seconds} sn`;

const buildAttentionQuery = (mode: AttentionMode, settings: AttentionSettings) => {
  const query = new URLSearchParams({
    difficulty: settings.difficulty,
    includeAnswer: 'true',
    durationSeconds: String(settings.durationSeconds),
  });

  return query.toString();
};

const normalizeCountAnswer = (value: string) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, Math.round(parsed));
};

const clampColorDisplayMs = (value: number) => {
  const normalized = Math.round(Number(value) / COLOR_SPEED_STEP_MS) * COLOR_SPEED_STEP_MS;
  return Math.min(COLOR_SPEED_MAX_MS, Math.max(COLOR_SPEED_MIN_MS, normalized));
};

const getPreviousOption = <T,>(items: readonly T[], current: T) => {
  const index = items.indexOf(current);
  return items[Math.max(0, index - 1)] || current;
};

const getNextOption = <T,>(items: readonly T[], current: T) => {
  const index = items.indexOf(current);
  return items[Math.min(items.length - 1, index + 1)] || current;
};

export const AttentionExerciseView: React.FC<{
  profile: any;
  mode: ModeConfig;
  onClose: () => void;
}> = ({ profile, mode, onClose }) => {
  const { user } = useAuth();
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const autoAdvanceTimeoutRef = useRef<number | null>(null);
  const colorFeedbackTimeoutRef = useRef<number | null>(null);
  const attentionMode = normalizeAttentionMode(mode);
  const [payload, setPayload] = useState<AttentionApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [phase, setPhase] = useState<AttentionPhase>('idle');
  const [countdown, setCountdown] = useState(3);
  const [remainingSeconds, setRemainingSeconds] = useState(60);
  const [currentColorIndex, setCurrentColorIndex] = useState(0);
  const [colorStats, setColorStats] = useState({ correct: 0, wrong: 0, missed: 0 });
  const [colorFeedback, setColorFeedback] = useState<{ choiceKey: string; result: 'correct' | 'wrong' } | null>(null);
  const [countAnswer, setCountAnswer] = useState('');
  const [selectedPuzzleOptionId, setSelectedPuzzleOptionId] = useState<string | null>(null);
  const [selectedDifferenceKeys, setSelectedDifferenceKeys] = useState<string[]>([]);
  const [settings, setSettings] = useState<AttentionSettings>(() => ({
    difficulty: 'orta',
    durationSeconds: 60,
    colorDisplayMs: 1000,
    fontSize: 'buyuk',
    fontFamily: 'GoogleSans',
    accentColor: 'default',
    enableCountdown: true,
  }));

  const exercise = payload?.exercise || null;
  const activeAccent = resolveAccentColor(settings.accentColor);
  const activeFontFamily = FONT_FAMILY_OPTIONS.find((item) => item.value === settings.fontFamily)?.css || settings.fontFamily;
  const isRunning = phase === 'running' || phase === 'countdown';
  const isCountingMode = COUNTING_MODES.has(attentionMode);
  const isStaticMode = STATIC_ATTENTION_MODES.has(attentionMode);
  const colorItems = exercise?.items || [];
  const colorTotal = colorStats.correct + colorStats.wrong + colorStats.missed;
  const currentColorItem = colorItems[currentColorIndex] || null;
  const introParagraphs = ATTENTION_INFO[attentionMode];
  const answerCount = typeof exercise?.answer === 'number' ? exercise.answer : null;
  const normalizedCountAnswer = normalizeCountAnswer(countAnswer);
  const isCountAnswerCorrect = answerCount !== null && normalizedCountAnswer === answerCount;
  const differenceAnswerKeys = useMemo(() => new Set((exercise?.differences || []).map((item) => cellKey(item.row, item.column))), [exercise?.differences]);
  const selectedDifferenceSet = useMemo(() => new Set(selectedDifferenceKeys), [selectedDifferenceKeys]);
  const differenceCorrectSelections = selectedDifferenceKeys.filter((key) => differenceAnswerKeys.has(key)).length;
  const differenceWrongSelections = selectedDifferenceKeys.filter((key) => !differenceAnswerKeys.has(key)).length;
  const puzzleAnswerOptionId = typeof exercise?.answer === 'object' && exercise.answer?.optionId ? String(exercise.answer.optionId) : null;
  const isPuzzleCorrect = Boolean(selectedPuzzleOptionId && puzzleAnswerOptionId && selectedPuzzleOptionId === puzzleAnswerOptionId);
  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || profile?.name || 'Kullanıcı';
  const userImage = user?.avatar || user?.media?.url || user?.profilePhoto?.url || null;
  const userInitials = userName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part.charAt(0).toUpperCase())
    .join('');

  const clearAutoAdvanceTimeout = () => {
    if (!autoAdvanceTimeoutRef.current) return;
    window.clearTimeout(autoAdvanceTimeoutRef.current);
    autoAdvanceTimeoutRef.current = null;
  };

  const clearColorFeedbackTimeout = () => {
    if (!colorFeedbackTimeoutRef.current) return;
    window.clearTimeout(colorFeedbackTimeoutRef.current);
    colorFeedbackTimeoutRef.current = null;
  };

  const loadExercise = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const query = buildAttentionQuery(attentionMode, settings);
      const response = await ApiService.getApi(`speed-reading/exercises/attention/${attentionMode}?${query}`);
      setPayload(response as AttentionApiResponse);
      const nextDuration = Number((response as AttentionApiResponse)?.exercise?.durationSeconds || (response as AttentionApiResponse)?.settings?.durationSeconds || settings.durationSeconds);
      setRemainingSeconds(Number.isFinite(nextDuration) ? nextDuration : settings.durationSeconds);
    } catch (error) {
      console.error('Dikkat egzersizi yüklenemedi:', error);
      setPayload(null);
      setLoadError('Egzersiz hazırlanamadı. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  }, [attentionMode, settings]);

  const handleReset = useCallback(() => {
    clearAutoAdvanceTimeout();
    clearColorFeedbackTimeout();
    setPhase('idle');
    setCountdown(3);
    setRemainingSeconds(Number(exercise?.durationSeconds || settings.durationSeconds));
    setCurrentColorIndex(0);
    setColorStats({ correct: 0, wrong: 0, missed: 0 });
    setColorFeedback(null);
    setCountAnswer('');
    setSelectedPuzzleOptionId(null);
    setSelectedDifferenceKeys([]);
  }, [exercise?.durationSeconds, settings.durationSeconds]);

  useEffect(() => {
    void loadExercise();
  }, [loadExercise]);

  useEffect(() => {
    if (phase !== 'idle') return;
    setRemainingSeconds(Number(exercise?.durationSeconds || settings.durationSeconds));
  }, [exercise?.durationSeconds, phase, settings.durationSeconds]);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => () => {
    clearAutoAdvanceTimeout();
    clearColorFeedbackTimeout();
  }, []);

  useEffect(() => {
    if (phase !== 'countdown' || countdown <= 0) return;
    const timer = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [countdown, phase]);

  useEffect(() => {
    if (phase !== 'countdown' || countdown !== 0) return;
    setPhase('running');
  }, [countdown, phase]);

  useEffect(() => {
    if (phase !== 'running' || !isCountingMode) return;
    const timer = window.setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          setPhase('answer');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [isCountingMode, phase]);

  const advanceColorItem = useCallback((result: 'correct' | 'wrong' | 'missed') => {
    clearAutoAdvanceTimeout();
    clearColorFeedbackTimeout();
    setColorFeedback(null);
    setColorStats((prev) => ({ ...prev, [result]: prev[result] + 1 }));
    setCurrentColorIndex((prev) => {
      if (prev >= colorItems.length - 1) {
        setPhase('review');
        return prev;
      }

      return prev + 1;
    });
  }, [colorItems.length]);

  useEffect(() => {
    if (phase !== 'running' || attentionMode !== 'rengi-bul' || !currentColorItem) return;
    if (colorFeedback) return;

    clearAutoAdvanceTimeout();
    autoAdvanceTimeoutRef.current = window.setTimeout(() => {
      advanceColorItem('missed');
    }, settings.colorDisplayMs);

    return () => clearAutoAdvanceTimeout();
  }, [advanceColorItem, attentionMode, colorFeedback, currentColorItem, currentColorIndex, phase, settings.colorDisplayMs]);

  const handleStart = () => {
    if (!exercise) return;
    setCurrentColorIndex(0);
    setColorStats({ correct: 0, wrong: 0, missed: 0 });
    setColorFeedback(null);
    setCountAnswer('');
    setSelectedPuzzleOptionId(null);
    setSelectedDifferenceKeys([]);
    setRemainingSeconds(Number(exercise.durationSeconds || settings.durationSeconds));

    if (settings.enableCountdown) {
      setCountdown(3);
      setPhase('countdown');
      return;
    }

    setPhase('running');
  };

  const handlePrimaryAction = () => {
    if (phase === 'review') {
      handleReset();
      void loadExercise();
      return;
    }

    if (isRunning) {
      handleReset();
      return;
    }

    if (phase === 'answer') {
      if (normalizedCountAnswer === null) return;
      setPhase('review');
      return;
    }

    handleStart();
  };

  const handleCloseExercise = async () => {
    if (document.fullscreenElement === fullscreenContainerRef.current) {
      try {
        await document.exitFullscreen();
      } catch (error) {
        console.error('Tam ekrandan çıkılamadı:', error);
      }
    }

    handleReset();
    onClose();
  };

  const handleToggleFullscreen = async () => {
    const container = fullscreenContainerRef.current;
    if (!container) return;

    try {
      if (document.fullscreenElement === container) {
        await document.exitFullscreen();
        return;
      }

      await container.requestFullscreen();
    } catch (error) {
      console.error('Tam ekran değiştirilemedi:', error);
    }
  };

  const handleColorChoice = (choiceKey: string) => {
    if (phase !== 'running' || attentionMode !== 'rengi-bul' || !currentColorItem) return;
    if (colorFeedback) return;

    const result = choiceKey === currentColorItem.answerKey ? 'correct' : 'wrong';
    clearAutoAdvanceTimeout();
    clearColorFeedbackTimeout();
    setColorFeedback({ choiceKey, result });
    colorFeedbackTimeoutRef.current = window.setTimeout(() => {
      advanceColorItem(result);
    }, 650);
  };

  const handlePuzzleSubmit = () => {
    if (!selectedPuzzleOptionId) return;
    setPhase('review');
  };

  const handleDifferenceSubmit = () => {
    if (selectedDifferenceKeys.length === 0) return;
    setPhase('review');
  };

  const renderShapeCell = (cell: PuzzleCell, options?: { active?: boolean; wrong?: boolean; selectable?: boolean; onClick?: () => void }) => {
    if (cell.missing) {
      return (
        <div className="flex aspect-square items-center justify-center rounded-[14px] border-2 border-dashed border-[#b7c8d0] bg-[#f6fafb] text-2xl font-black text-[#8aa0aa]">
          ?
        </div>
      );
    }

    return (
      <button
        type="button"
        disabled={!options?.selectable}
        onClick={options?.onClick}
        className="flex aspect-square items-center justify-center rounded-[14px] border transition-all"
        style={{
          borderColor: options?.wrong ? '#ef8b8b' : options?.active ? activeAccent : '#e1e9ed',
          backgroundColor: options?.active ? `${activeAccent}14` : '#fbfdfe',
          boxShadow: options?.active ? `0 0 0 2px ${activeAccent}22` : 'none',
          cursor: options?.selectable ? 'pointer' : 'default',
        }}
      >
        <span className="inline-flex items-center justify-center text-[32px] leading-[1.15]" style={{ color: cell.colorHex || activeAccent }}>
          {shapeGlyph(cell.shape)}
        </span>
      </button>
    );
  };

  const renderMainStage = () => {
    if (loading) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-4">
          <Loader2 className="h-9 w-9 animate-spin text-[#2f5563]" />
          <p className="text-[20px] font-bold text-[#2e6276]">Egzersiz hazırlanıyor...</p>
        </div>
      );
    }

    if (loadError) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
          <p className="text-[18px] font-bold text-[#9b4d4d]">{loadError}</p>
          <button
            type="button"
            onClick={() => { void loadExercise(); }}
            className="rounded-xl px-5 py-2.5 text-sm font-bold text-white"
            style={getThemeChromeButtonStyle()}
          >
            Tekrar Dene
          </button>
        </div>
      );
    }

    if (!exercise) return null;

    if (phase === 'idle') {
      return (
        <div className="flex h-full items-center justify-center px-8 text-center">
          <div className="max-w-[920px]">
            {introParagraphs.map((paragraph, index) => (
              <p
                key={paragraph}
                className={`mx-auto text-[20px] font-bold leading-[1.55] text-[#2e6276] ${index > 0 ? 'mt-8' : ''}`}
                style={{ fontFamily: activeFontFamily }}
              >
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      );
    }

    if (phase === 'countdown') {
      return (
        <div className="flex h-full items-center justify-center">
          <span className="text-7xl font-extrabold text-[#18485c]">{countdown}</span>
        </div>
      );
    }

    if (attentionMode === 'rengi-bul' && phase === 'running') {
      return (
        <div className="flex h-full flex-col items-center justify-center px-8">
          <div className="mb-10 text-center text-sm font-bold uppercase tracking-[0.12em] text-[#8aa0aa]">
            {currentColorIndex + 1} / {colorItems.length}
          </div>
          <div
            className="flex min-h-[130px] items-center justify-center text-center text-[clamp(3.2rem,7vw,6.8rem)] font-black leading-[1.1]"
            style={{ color: currentColorItem?.textColorHex || activeAccent, fontFamily: activeFontFamily }}
          >
            <span className="whitespace-nowrap">{currentColorItem?.text}</span>
          </div>
          <div className="mt-5 h-7 text-sm font-black">
            {colorFeedback ? (
              <span className={colorFeedback.result === 'correct' ? 'text-[#33835a]' : 'text-[#a14f4f]'}>
                {colorFeedback.result === 'correct' ? 'Doğru' : 'Yanlış'}
              </span>
            ) : null}
          </div>
          <div className="mt-12 grid w-full max-w-[720px] grid-cols-2 gap-3 md:grid-cols-5">
            {(exercise.choices || []).map((choice) => {
              const isSelected = colorFeedback?.choiceKey === choice.key;
              const selectedIsCorrect = colorFeedback?.result === 'correct';

              return (
                <button
                  key={choice.key}
                  type="button"
                  onClick={() => handleColorChoice(choice.key)}
                  disabled={Boolean(colorFeedback)}
                  className="flex h-12 items-center justify-center gap-2 rounded-xl border bg-white text-sm font-black text-[#285367] transition-colors hover:border-[#c0d3dc] disabled:cursor-default"
                  style={{
                    borderColor: isSelected ? (selectedIsCorrect ? '#7ccf92' : '#ef9a9a') : '#dce8ee',
                    backgroundColor: isSelected ? (selectedIsCorrect ? '#f0fbf3' : '#fff3f3') : '#ffffff',
                  }}
                >
                  <span className="h-4 w-4 rounded-full" style={{ backgroundColor: choice.hex }} />
                  {choice.label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    if (isCountingMode && phase === 'running') {
      const boardFontSize = settings.fontSize === 'kucuk'
        ? '18px'
        : settings.fontSize === 'orta'
          ? '22px'
          : settings.fontSize === 'buyuk'
            ? '26px'
            : '30px';

      return (
        <div className="h-full min-w-0 overflow-auto bg-white px-8 py-7">
          <div className="mx-auto w-full min-w-0 max-w-[1080px] rounded-2xl border border-[#dfe9ee] bg-[#fbfdfe] px-6 py-5">
            <div className="mb-5 flex min-w-0 items-center justify-between gap-4">
              <div className="min-w-0 break-words text-[18px] font-black text-[#285367]">{exercise.prompt}</div>
              <div className="shrink-0 rounded-full bg-[#eef6f8] px-4 py-2 text-sm font-black text-[#2e6276]">
                Hedef: {exercise.target}
              </div>
            </div>
            <div
              className="min-w-0 select-none whitespace-pre-wrap font-black leading-[1.55] tracking-[0.02em] text-[#111111]"
              style={{
                fontFamily: activeFontFamily,
                fontSize: boardFontSize,
                overflowWrap: attentionMode === 'harf-sayma' ? 'anywhere' : 'break-word',
                wordBreak: attentionMode === 'harf-sayma' ? 'break-all' : 'normal',
              }}
            >
              {(exercise.board?.lines || []).map((line, index) => (
                <div key={`${line}-${index}`}>{line}</div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (phase === 'answer') {
      return (
        <div className="flex h-full items-center justify-center px-8">
          <div className="w-full max-w-[520px] rounded-2xl border border-[#dfe9ee] bg-[#fbfdfe] p-7 text-center">
            <div className="text-sm font-bold uppercase tracking-[0.1em] text-[#8aa0aa]">Süre Doldu</div>
            <div className="mt-3 text-[28px] font-black text-[#174e67]">{exercise.answerPrompt || 'Cevabınızı Yazınız'}</div>
            <input
              value={countAnswer}
              onChange={(event) => setCountAnswer(event.target.value.replace(/[^\d]/g, ''))}
              inputMode="numeric"
              className="mt-6 h-16 w-full rounded-xl border border-[#d5e3e9] bg-white px-5 text-center text-[30px] font-black text-[#174e67] outline-none focus:border-[#86b5c7]"
              autoFocus
            />
          </div>
        </div>
      );
    }

    if (attentionMode === 'dikkat-puzzle' && phase === 'running') {
      return (
        <div className="flex h-full items-center justify-center overflow-auto p-6">
          <div className="grid w-full max-w-[980px] gap-8 lg:grid-cols-[1fr_280px]">
            <div className="rounded-2xl border border-[#dfe9ee] bg-white p-5">
              <div
                className="grid gap-3"
                style={{ gridTemplateColumns: `repeat(${exercise.grid?.length || 3}, minmax(0, 1fr))` }}
              >
                {(exercise.grid || []).flat().map((cell) => (
                  <React.Fragment key={cellKey(cell.row, cell.column)}>
                    {renderShapeCell(cell)}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-[#dfe9ee] bg-[#fbfdfe] p-5">
              <div className="mb-4 text-sm font-black text-[#285367]">{exercise.prompt}</div>
              <div className="grid grid-cols-2 gap-3">
                {(exercise.options || []).map((option) => {
                  const isActive = selectedPuzzleOptionId === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setSelectedPuzzleOptionId(option.id)}
                      className="rounded-xl border bg-white p-3 transition-all"
                      style={getThemedOptionButtonStyle(isActive)}
                    >
                      <div className="text-xs font-black text-[#6f8792]">{option.id}</div>
                      <div className="mt-2 text-[34px] leading-none" style={{ color: option.colorHex }}>{shapeGlyph(option.shape)}</div>
                      <div className="mt-1 text-[11px] font-bold text-[#6f8792]">{shapeLabel(option.shape)}</div>
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={handlePuzzleSubmit}
                disabled={!selectedPuzzleOptionId}
                className="mt-5 h-11 w-full rounded-lg text-sm font-black text-white disabled:cursor-not-allowed disabled:bg-[#dfe7ea] disabled:text-[#7f929c]"
                style={selectedPuzzleOptionId ? getThemeChromeButtonStyle() : undefined}
              >
                Cevabı Kaydet
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (attentionMode === 'iki-resim-arasindaki-fark' && phase === 'running') {
      const renderScene = (scene: AttentionExercisePayload['leftScene'], selectable: boolean) => (
        <div
          className="grid gap-2 rounded-2xl border border-[#dfe9ee] bg-white p-4"
          style={{ gridTemplateColumns: `repeat(${scene?.columns || 4}, minmax(0, 1fr))` }}
        >
          {(scene?.cells || []).map((cell) => {
            const key = cellKey(cell.row, cell.column);
            const isSelected = selectedDifferenceSet.has(key);
            return (
              <React.Fragment key={key}>
                {renderShapeCell(cell, {
                  active: isSelected,
                  selectable,
                  onClick: selectable
                    ? () => {
                      setSelectedDifferenceKeys((prev) => (
                        prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
                      ));
                    }
                    : undefined,
                })}
              </React.Fragment>
            );
          })}
        </div>
      );

      return (
        <div className="flex h-full flex-col overflow-auto p-5">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div className="text-[18px] font-black text-[#285367]">{exercise.prompt}</div>
            <div className="rounded-full bg-[#eef6f8] px-4 py-2 text-sm font-black text-[#2e6276]">
              Seçilen: {selectedDifferenceKeys.length} / {exercise.differenceCount || 0}
            </div>
          </div>
          <div className="grid flex-1 gap-5 lg:grid-cols-2">
            {renderScene(exercise.leftScene, false)}
            {renderScene(exercise.rightScene, true)}
          </div>
          <button
            type="button"
            onClick={handleDifferenceSubmit}
            disabled={selectedDifferenceKeys.length === 0}
            className="mx-auto mt-5 h-11 w-full max-w-[320px] rounded-lg text-sm font-black text-white disabled:cursor-not-allowed disabled:bg-[#dfe7ea] disabled:text-[#7f929c]"
            style={selectedDifferenceKeys.length > 0 ? getThemeChromeButtonStyle() : undefined}
          >
            Seçimleri Kontrol Et
          </button>
        </div>
      );
    }

    if (phase === 'review') {
      const countDifference = answerCount !== null && normalizedCountAnswer !== null ? Math.abs(answerCount - normalizedCountAnswer) : null;

      return (
        <div className="h-full overflow-y-auto p-8">
          <div className="mx-auto max-w-[900px] space-y-5">
            <div className="flex items-center gap-3 text-[#2f6d85]">
              <CheckCircle2 className="h-7 w-7" />
              <div className="text-2xl font-black">Egzersiz Tamamlandı</div>
            </div>

            {attentionMode === 'rengi-bul' ? (
              <div className="rounded-2xl border border-[#e1e8ed] bg-[#f8fbfc] p-6">
                <div className="text-[18px] font-black text-[#285367]">Renk tepkisi kaydedildi.</div>
                <p className="mt-2 text-sm leading-6 text-[#65808c]">
                  Yeni egzersiz başlattığınızda farklı renk dizileriyle çalışma yenilenir.
                </p>
              </div>
            ) : isCountingMode ? (
              <div className="grid gap-4 md:grid-cols-3">
                <ResultTile label="Cevabınız" value={normalizedCountAnswer ?? '-'} />
                <ResultTile label="Doğru Cevap" value={answerCount ?? '-'} />
                <ResultTile label="Fark" value={countDifference ?? '-'} tone={isCountAnswerCorrect ? 'success' : 'danger'} />
              </div>
            ) : attentionMode === 'dikkat-puzzle' ? (
              <div className="grid gap-4 md:grid-cols-3">
                <ResultTile label="Seçim" value={selectedPuzzleOptionId || '-'} />
                <ResultTile label="Doğru" value={puzzleAnswerOptionId || '-'} />
                <ResultTile label="Sonuç" value={isPuzzleCorrect ? 'Doğru' : 'Yanlış'} tone={isPuzzleCorrect ? 'success' : 'danger'} />
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-4">
                <ResultTile label="Doğru Seçim" value={differenceCorrectSelections} />
                <ResultTile label="Yanlış Seçim" value={differenceWrongSelections} />
                <ResultTile label="Beklenen" value={exercise.differenceCount || 0} />
                <ResultTile label="Sonuç" value={differenceWrongSelections === 0 && differenceCorrectSelections === (exercise.differenceCount || 0) ? 'Tam' : 'Eksik'} />
              </div>
            )}

            <div className="rounded-2xl border border-[#e1e8ed] bg-[#f8fbfc] p-5">
              <div className="flex items-start gap-3">
                <CircleHelp className="mt-0.5 h-5 w-5 text-[#6d8b98]" />
                <div className="text-sm leading-6 text-[#65808c]">
                  Aynı egzersizi yeniden başlattığınızda yeni ve özgün bir içerik üretir. Zorluk ayarını sağ üstteki ayarlar düğmesinden değiştirebilirsiniz.
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const primaryLabel = phase === 'review'
    ? 'Yeni Egzersiz'
    : phase === 'answer'
      ? 'Cevabı Kaydet'
      : isRunning
        ? 'Durdur'
        : 'Başlat';
  const primaryDisabled = loading || Boolean(loadError) || (phase === 'answer' && normalizedCountAnswer === null);

  return (
    <div ref={fullscreenContainerRef} className={`${isFullscreen ? 'h-screen p-3' : 'h-full p-6'} bg-[#f3f5f7]`}>
      <div className={`${isFullscreen ? 'max-w-none' : 'max-w-[1260px]'} mx-auto h-full min-w-0`}>
        <div className="relative flex h-full min-w-0 overflow-hidden gap-5 rounded-xl border border-[#e6edf1] bg-[#f8fafb] px-6 py-5">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TimerReset className="h-[19px] w-[19px]" style={getThemeChromeTextStyle()} />
                <div className="text-[20px] font-bold" style={getThemeChromeTextStyle()}>{mode.name}</div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setSettingsOpen(true)}
                  disabled={isRunning}
                  className={EXERCISE_TOOL_BUTTON_CLASS}
                  title="Ayarlar"
                >
                  <Settings2 className="h-5 w-5" />
                </button>
                <button type="button" onClick={() => { void handleToggleFullscreen(); }} className={EXERCISE_TOOL_BUTTON_CLASS} title={isFullscreen ? 'Tam Ekrandan Çık' : 'Tam Ekran'}>
                  {isFullscreen ? <Shrink className="h-5 w-5" /> : <Expand className="h-5 w-5" />}
                </button>
                <button type="button" onClick={() => { void handleCloseExercise(); }} className={EXERCISE_TOOL_BUTTON_CLASS} title="Kapat">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="min-h-0 min-w-0 flex-1 overflow-hidden rounded-[10px] border border-[#e8edf0] bg-white">
              {renderMainStage()}
            </div>
          </div>

          <aside className="flex h-full min-h-0 w-[320px] min-w-[320px] shrink-0 flex-col rounded-xl border border-[#e0eaee] bg-white p-5">
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="flex h-[56px] items-start gap-3">
                {userImage ? (
                  <img src={userImage} alt={userName} className="h-11 w-11 shrink-0 rounded-full bg-[#daf0f7] object-cover" />
                ) : (
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#daf0f7] font-bold text-[#146f8e]">
                    {userInitials || 'K'}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="whitespace-normal break-words text-sm font-bold leading-5 text-[#355c68]">{userName}</div>
                  <div className="whitespace-normal break-words text-xs leading-4 text-[#94a8b3]">Dikkat ve Odaklanma</div>
                </div>
              </div>

              <div className="mt-5 flex h-[230px] flex-col items-center text-center">
                <div className="flex h-[132px] w-full items-center justify-center">
                  <div className="flex h-[112px] w-[148px] items-center justify-center rounded-[2rem] bg-[#f7fafc] px-5 py-4">
                    <ModeIllustration mode={mode} />
                  </div>
                </div>
                <div className="flex min-h-[86px] w-full items-center justify-center">
                  <h3 className="max-w-full whitespace-normal break-words text-[34px] font-black leading-[0.98]" style={getThemeChromeTextStyle()}>{mode.name}</h3>
                </div>
              </div>

              {attentionMode === 'rengi-bul' ? (
                <div className="mt-5 grid h-[100px] grid-cols-2 gap-x-8 gap-y-5 text-center">
                  <div>
                    <div className="text-[14px] text-[#b1b8bd]">Doğru</div>
                    <div className="mt-1 text-[22px] font-black leading-none text-[#194e62]">{colorStats.correct}</div>
                  </div>
                  <div>
                    <div className="text-[14px] text-[#b1b8bd]">Yanlış</div>
                    <div className="mt-1 text-[22px] font-black leading-none text-[#194e62]">{colorStats.wrong}</div>
                  </div>
                  <div>
                    <div className="text-[14px] text-[#b1b8bd]">Boş</div>
                    <div className="mt-1 text-[22px] font-black leading-none text-[#194e62]">{colorStats.missed}</div>
                  </div>
                  <div>
                    <div className="text-[14px] text-[#b1b8bd]">Hız</div>
                    <div className="mt-1 text-[22px] font-black leading-none text-[#194e62]">{settings.colorDisplayMs}ms</div>
                  </div>
                </div>
              ) : (
                <div className="mt-5 grid h-[60px] grid-cols-2 gap-6 text-center">
                  <div>
                    <div className="text-[14px] text-[#b1b8bd]">Zorluk</div>
                    <div className="mt-1 text-[20px] font-black leading-none text-[#194e62]">
                      {DIFFICULTY_OPTIONS.find((item) => item.value === settings.difficulty)?.label || 'Orta'}
                    </div>
                  </div>
                  <div>
                    <div className="text-[14px] text-[#b1b8bd]">Süre</div>
                    <div className="mt-1 text-[20px] font-black leading-none text-[#194e62]">
                      {isStaticMode ? 'Serbest' : durationLabel(settings.durationSeconds)}
                    </div>
                  </div>
                </div>
              )}

              <div className="min-h-[252px]">
                {phase === 'idle' ? <SidebarPreparationCard paragraphs={introParagraphs} fontFamily={activeFontFamily} /> : null}
              </div>
            </div>

            <div className="h-[210px] shrink-0 pt-4">
              <p className="text-xs text-[#98a8b1]">{isCountingMode ? 'Kalan Süre' : attentionMode === 'rengi-bul' ? 'İlerleme' : 'Durum'}</p>
              <p className="mt-1 text-[44px] font-black leading-none" style={getThemeChromeTextStyle()}>
                {isCountingMode
                  ? formatClock(phase === 'review' ? 0 : remainingSeconds)
                  : attentionMode === 'rengi-bul'
                    ? `${Math.min(currentColorIndex + 1, Math.max(colorItems.length, 1))}/${Math.max(colorItems.length, 1)}`
                    : phase === 'review' ? 'Bitti' : phase === 'running' ? 'Aktif' : 'Hazır'}
              </p>

              <button
                type="button"
                onClick={handlePrimaryAction}
                disabled={primaryDisabled}
                className={`mt-4 inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg text-base font-bold transition-colors ${primaryDisabled
                  ? 'cursor-not-allowed bg-[#dfe7ea] text-[#7f929c]'
                  : 'text-white'
                  }`}
                style={primaryDisabled ? undefined : getThemeChromeButtonStyle()}
              >
                {phase === 'review' ? <RotateCcw className="h-4 w-4" /> : isRunning ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                {primaryLabel}
              </button>

              {phase !== 'review' ? (
                <button
                  type="button"
                  onClick={handleReset}
                  className="mt-4 h-12 w-full rounded-lg bg-[#f1f3f5] text-base font-bold text-[#2d6175] transition-colors hover:bg-[#e7ecef]"
                >
                  Sıfırla
                </button>
              ) : (
                <div className="mt-4 h-12" />
              )}
            </div>
          </aside>

          {settingsOpen ? (
            <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/55 backdrop-blur-[4px]">
              <div className="w-[920px] rounded-xl bg-white p-6 shadow-lg">
                <div className="mb-5 flex items-center justify-between">
                  <h4 className="text-xl font-bold" style={getThemedLabelStyle()}>Egzersiz Ayarları</h4>
                  <button type="button" className="text-gray-500 hover:text-gray-700" onClick={() => setSettingsOpen(false)}>
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-7">
                  <div className="space-y-5 pr-2">
                    <SettingStepper
                      label="Zorluk"
                      value={DIFFICULTY_OPTIONS.find((item) => item.value === settings.difficulty)?.label || 'Orta'}
                      onDecrease={() => {
                        setSettings((prev) => ({
                          ...prev,
                          difficulty: getPreviousOption(DIFFICULTY_OPTIONS.map((item) => item.value), prev.difficulty),
                        }));
                        handleReset();
                      }}
                      onIncrease={() => {
                        setSettings((prev) => ({
                          ...prev,
                          difficulty: getNextOption(DIFFICULTY_OPTIONS.map((item) => item.value), prev.difficulty),
                        }));
                        handleReset();
                      }}
                    />

                    {attentionMode === 'rengi-bul' ? (
                      <SettingStepper
                        label="Hız"
                        value={`${settings.colorDisplayMs}ms`}
                        onDecrease={() => {
                          setSettings((prev) => ({
                            ...prev,
                            colorDisplayMs: clampColorDisplayMs(prev.colorDisplayMs - COLOR_SPEED_STEP_MS),
                          }));
                          handleReset();
                        }}
                        onIncrease={() => {
                          setSettings((prev) => ({
                            ...prev,
                            colorDisplayMs: clampColorDisplayMs(prev.colorDisplayMs + COLOR_SPEED_STEP_MS),
                          }));
                          handleReset();
                        }}
                      />
                    ) : !isStaticMode ? (
                      <SettingStepper
                        label="Süre"
                        value={durationLabel(settings.durationSeconds)}
                        onDecrease={() => {
                          setSettings((prev) => ({
                            ...prev,
                            durationSeconds: getPreviousOption(DURATION_OPTIONS, prev.durationSeconds as typeof DURATION_OPTIONS[number]),
                          }));
                          handleReset();
                        }}
                        onIncrease={() => {
                          setSettings((prev) => ({
                            ...prev,
                            durationSeconds: getNextOption(DURATION_OPTIONS, prev.durationSeconds as typeof DURATION_OPTIONS[number]),
                          }));
                          handleReset();
                        }}
                      />
                    ) : null}

                    <SettingsToggleRow
                      label="Başlamadan önce geriye say"
                      checked={settings.enableCountdown}
                      onChange={() => setSettings((prev) => ({ ...prev, enableCountdown: !prev.enableCountdown }))}
                    />
                  </div>

                  <div className="space-y-5 border-l border-[#edf2f5] pl-7">
                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Boyutu</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {FONT_SIZE_LABELS[settings.fontSize]}
                        </span>
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {FONT_SIZE_OPTIONS.map((sizeOption) => {
                          const isActive = settings.fontSize === sizeOption.value;
                          return (
                            <button
                              key={sizeOption.value}
                              type="button"
                              onClick={() => setSettings((prev) => ({ ...prev, fontSize: sizeOption.value }))}
                              className="h-10 rounded-lg border transition-colors"
                              style={getThemedOptionButtonStyle(isActive)}
                            >
                              <span className={`font-semibold ${sizeOption.preview}`}>{sizeOption.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Karakteri</p>
                      <div className="grid grid-cols-2 gap-2">
                        {FONT_FAMILY_OPTIONS.map((fontOption) => {
                          const isActive = settings.fontFamily === fontOption.value;
                          return (
                            <button
                              key={fontOption.value}
                              type="button"
                              onClick={() => setSettings((prev) => ({ ...prev, fontFamily: fontOption.value }))}
                              className="h-10 rounded-lg border text-sm transition-colors"
                              style={{ ...getThemedOptionButtonStyle(isActive), fontFamily: fontOption.css }}
                            >
                              {fontOption.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Vurgu Rengi</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {ACCENT_COLOR_OPTIONS.find((colorOption) => colorOption.value === settings.accentColor)?.label || 'Varsayılan'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        {ACCENT_COLOR_OPTIONS.map((colorOption) => {
                          const isActive = settings.accentColor === colorOption.value;
                          return (
                            <button
                              key={colorOption.value}
                              type="button"
                              onClick={() => setSettings((prev) => ({ ...prev, accentColor: colorOption.value }))}
                              className="h-7 w-7 rounded-full border-2 transition-transform hover:scale-105"
                              style={{ ...getThemedSwatchStyle(isActive, colorOption.hex), backgroundColor: colorOption.hex }}
                              title={colorOption.label}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const ResultTile: React.FC<{ label: string; value: React.ReactNode; tone?: 'success' | 'danger' }> = ({ label, value, tone }) => (
  <div className={`rounded-2xl border px-5 py-4 ${tone === 'success'
    ? 'border-[#cfe6d5] bg-[#f4fbf6]'
    : tone === 'danger'
      ? 'border-[#f0d7d7] bg-[#fff7f7]'
      : 'border-[#dce8ed] bg-[#f8fbfc]'
    }`}>
    <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[#8aa0ab]">{label}</div>
    <div className={`mt-2 text-[30px] font-black ${tone === 'success' ? 'text-[#33835a]' : tone === 'danger' ? 'text-[#a14f4f]' : 'text-[#194e62]'}`}>
      {value}
    </div>
  </div>
);

const SettingStepper: React.FC<{
  label: string;
  value: string;
  onDecrease: () => void;
  onIncrease: () => void;
}> = ({ label, value, onDecrease, onIncrease }) => (
  <div className="overflow-hidden rounded-xl border border-[#d9e6eb] bg-white">
    <div className="grid h-12 grid-cols-[minmax(0,1fr)_54px_96px_54px] items-center">
      <div className="border-r border-[#e7eef2] px-4 text-sm font-bold uppercase tracking-[0.08em] text-[#8b9ca5]">{label}</div>
      <button type="button" onClick={onDecrease} className="h-full border-r border-[#e7eef2] text-xl font-black text-[#b0bec6] transition-colors hover:bg-[#f7fbfd] hover:text-[#6f8a96]">
        &laquo;
      </button>
      <div className="text-center text-base font-black text-[#5d7380]">{value}</div>
      <button type="button" onClick={onIncrease} className="h-full border-l border-[#e7eef2] text-xl font-black text-[#b0bec6] transition-colors hover:bg-[#f7fbfd] hover:text-[#6f8a96]">
        &raquo;
      </button>
    </div>
  </div>
);

const SwitchKnob: React.FC<{ checked: boolean }> = ({ checked }) => (
  <span
    className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow-[0_2px_8px_rgba(43,62,76,0.18)] transition-all ${checked ? 'left-[34px]' : 'left-1'}`}
  />
);

const SettingsToggleRow: React.FC<{ label: string; checked: boolean; onChange: () => void }> = ({ label, checked, onChange }) => (
  <div className="overflow-hidden rounded-xl border border-[#d9e6eb] bg-white">
    <div className="flex min-h-14 items-center gap-4 px-4 py-3">
      <span className="flex-1 text-sm font-bold uppercase tracking-[0.08em] text-[#8b9ca5]">{label}</span>
      <button
        type="button"
        onClick={onChange}
        className={`relative h-8 w-16 shrink-0 rounded-full transition-colors ${checked ? 'bg-[#b6d2ad]' : 'bg-[#d7e1e7]'}`}
        aria-pressed={checked}
      >
        <SwitchKnob checked={checked} />
      </button>
    </div>
  </div>
);
